import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CompletedListResponse,
  DraftListResponse,
  DraftResponse,
  DuplicateResponse,
  GenerateResponse,
  PreviewResponse,
  ShareResponse,
  UploadResponse,
  ValidateResponse,
  CatalogBusinessInfo,
  CatalogDraft,
  CatalogPaletteType,
  CatalogTemplate,
} from '@core/interfaces/catalog.interface';
import {
  catchError,
  interval,
  map,
  Observable,
  switchMap,
  takeWhile,
  throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';

interface CreateDraftPayload {
  phone: string;
  template?: CatalogTemplate;
  palette_type?: CatalogPaletteType;
  color_primary?: string | null;
  color_primary_pastel?: string | null;
}

interface UpdateDraftPayload {
  template?: CatalogTemplate;
  style?: 'style1' | 'style2' | 'style3';
  palette_type?: CatalogPaletteType;
  color_primary?: string | null;
  color_primary_pastel?: string | null;
  logo?: string | null;
  business_info?: CatalogBusinessInfo;
}

interface ProductPayload {
  name: string;
  description?: string;
  price?: string;
  images?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class CatalogsService {
  public urlBackEnd: string = environment.base_url;

  constructor(private http: HttpClient) {}

  private get authHeaders(): HttpHeaders {
    return new HttpHeaders()
      .set('Authorization', `Bearer ${localStorage.getItem('token_conhector') || ''}`)
      .set('idUser', localStorage.getItem('user_conhector') || '')
      .set('iduser', localStorage.getItem('user_conhector') || '');
  }

  private normalizePhone(phone: string): string {
    return (phone || '').trim().replace(/^\+/, '');
  }

  // ─────────────── Borradores ───────────────

  listDrafts(
    phone: string,
    page = 0,
    pageSize = 10,
    opts: { status?: string; sort?: string; search?: string } = {},
  ): Observable<DraftListResponse> {
    let params = new HttpParams()
      .set('phone', this.normalizePhone(phone))
      .set('page', String(page))
      .set('pageSize', String(pageSize));

    if (opts.status) params = params.set('status', opts.status);
    if (opts.sort) params = params.set('sort', opts.sort);
    if (opts.search) params = params.set('search', opts.search);

    return this.http
      .get<DraftListResponse>(`${this.urlBackEnd}/catalogs/bot/drafts`, {
        params,
        headers: this.authHeaders,
      })
      .pipe(catchError(this.handleError));
  }

  createDraft(payload: CreateDraftPayload): Observable<DraftResponse> {
    return this.http
      .post<DraftResponse>(`${this.urlBackEnd}/catalogs/bot/drafts`, {
        ...payload,
        phone: this.normalizePhone(payload.phone),
      }, { headers: this.authHeaders })
      .pipe(catchError(this.handleError));
  }

  getDraft(id: string): Observable<DraftResponse> {
    return this.http
      .get<DraftResponse>(`${this.urlBackEnd}/catalogs/bot/drafts/${id}`, {
        headers: this.authHeaders,
      })
      .pipe(catchError(this.handleError));
  }

  updateDraft(id: string, payload: UpdateDraftPayload): Observable<DraftResponse> {
    return this.http
      .put<DraftResponse>(`${this.urlBackEnd}/catalogs/bot/drafts/${id}`, payload, {
        headers: this.authHeaders,
      })
      .pipe(catchError(this.handleError));
  }

  duplicateDraft(id: string): Observable<DuplicateResponse> {
    return this.http
      .post<DuplicateResponse>(
        `${this.urlBackEnd}/catalogs/bot/drafts/${id}/duplicate`,
        {},
        { headers: this.authHeaders },
      )
      .pipe(catchError(this.handleError));
  }

  // ─────────────── Productos (por índice) ───────────────

  addProduct(id: string, payload: ProductPayload): Observable<DraftResponse> {
    return this.http
      .post<DraftResponse>(
        `${this.urlBackEnd}/catalogs/bot/drafts/${id}/products`,
        payload,
        { headers: this.authHeaders },
      )
      .pipe(catchError(this.handleError));
  }

  updateProduct(
    id: string,
    index: number,
    payload: ProductPayload,
  ): Observable<DraftResponse> {
    return this.http
      .put<DraftResponse>(
        `${this.urlBackEnd}/catalogs/bot/drafts/${id}/products/${index}`,
        payload,
        { headers: this.authHeaders },
      )
      .pipe(catchError(this.handleError));
  }

  deleteProduct(id: string, index: number): Observable<DraftResponse> {
    return this.http
      .delete<DraftResponse>(
        `${this.urlBackEnd}/catalogs/bot/drafts/${id}/products/${index}`,
        { headers: this.authHeaders },
      )
      .pipe(catchError(this.handleError));
  }

  // ─────────────── Validar / Preview / Generar ───────────────

  validateDraft(id: string): Observable<ValidateResponse> {
    return this.http
      .post<ValidateResponse>(
        `${this.urlBackEnd}/catalogs/bot/drafts/${id}/validate`,
        {},
        { headers: this.authHeaders },
      )
      .pipe(catchError(this.handleError));
  }

  getPreview(id: string): Observable<PreviewResponse> {
    return this.http
      .get<PreviewResponse>(
        `${this.urlBackEnd}/catalogs/bot/drafts/${id}/preview`,
        { headers: this.authHeaders },
      )
      .pipe(catchError(this.handleError));
  }

  generateCatalog(id: string, phone: string): Observable<GenerateResponse> {
    return this.http
      .post<GenerateResponse>(
        `${this.urlBackEnd}/catalogs/bot/drafts/${id}/generate`,
        { phone: this.normalizePhone(phone) },
        { headers: this.authHeaders },
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Sondea el borrador cada `everyMs` hasta que `status` deja de ser
   * 'generating'/'queued'. Emite el draft en cada intento y completa al terminar.
   */
  pollDraftUntilDone(
    id: string,
    everyMs = 4000,
    maxTries = 60,
  ): Observable<CatalogDraft> {
    let tries = 0;
    return interval(everyMs).pipe(
      switchMap(() => this.getDraft(id)),
      map((resp) => resp.draft),
      takeWhile((draft) => {
        tries++;
        const pending =
          draft.status === 'generating' || draft.status === 'queued';
        return pending && tries < maxTries;
      }, true),
    );
  }

  // ─────────────── Completados ───────────────

  listCompleted(
    phone: string,
    page = 0,
    pageSize = 10,
  ): Observable<CompletedListResponse> {
    const params = new HttpParams()
      .set('phone', this.normalizePhone(phone))
      .set('page', String(page))
      .set('pageSize', String(pageSize));

    return this.http
      .get<CompletedListResponse>(`${this.urlBackEnd}/catalogs/bot/completed`, {
        params,
        headers: this.authHeaders,
      })
      .pipe(catchError(this.handleError));
  }

  getCompleted(id: string): Observable<{ ok: boolean; catalog: unknown }> {
    return this.http
      .get<{ ok: boolean; catalog: unknown }>(
        `${this.urlBackEnd}/catalogs/bot/completed/${id}`,
        { headers: this.authHeaders },
      )
      .pipe(catchError(this.handleError));
  }

  shareCatalog(id: string): Observable<ShareResponse> {
    return this.http
      .post<ShareResponse>(
        `${this.urlBackEnd}/catalogs/bot/completed/${id}/share`,
        {},
        { headers: this.authHeaders },
      )
      .pipe(catchError(this.handleError));
  }

  // ─────────────── Subida de imágenes (Firebase vía /globales) ───────────────

  uploadImage(file: File, carpeta = 'catalogs'): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('carpeta', carpeta);

    return this.http
      .post<UploadResponse>(`${this.urlBackEnd}/globales/upload`, formData, {
        headers: this.authHeaders,
      })
      .pipe(
        map((resp) => {
          const url = resp?.data?.fileUrl || resp?.data?.downloadURL;
          if (!url) {
            throw new Error('La respuesta de carga no incluyó una URL');
          }
          return url;
        }),
        catchError(this.handleError),
      );
  }

  private handleError(error: HttpErrorResponse) {
    console.error(
      'HTTP Error (catalogs):',
      error.status,
      error.error?.error || error.error?.msg || error.message,
    );
    return throwError(() => error);
  }
}
